import { MCDANIEL_FOLDER_ID } from "constants/settings.constants";
import { create } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";

import { ForecastState, ForecastStore } from "./forecastTypes";

export const initialForecastState: ForecastState = {
  selectedTypeWell: null,
  hasUnsavedChanges: false,
  isCreatingTypeWell: false,
  temporaryForecast: null,
  syncState: { type: null, id: null },

  checkedGlobalForecasts: [],
  checkedGlobalTypeWells: [],
  originalCheckedGlobalTypeWells: [],

  checkedForecasts: [],
  checkedTypeWells: [],
  checkedForecastKeys: [MCDANIEL_FOLDER_ID],
  checkedTypeWellKeys: [],

  expandedForecastKeys: [],
  expandedTypeWellKeys: [],

  useMcDanielForecasts: true,
  useNoviForecasts: false,
  useWorkingForecasts: false,

  isTypeWellSavable: false,
  hasWellDataError: false,
  hasTypeWellSaveSucceeded: false,

  bEditableSegments: {},
  typeWellTemplateNames: {}
};

export const useForecastStore = create<ForecastStore>()(
  subscribeWithSelector((set, get) => ({
    ...initialForecastState,

    setSelectedTypeWell: (typeWell) => set({ selectedTypeWell: typeWell }),
    setHasUnsavedChanges: (changed) => set({ hasUnsavedChanges: changed }),
    setIsCreatingTypeWell: (creating) => set({ isCreatingTypeWell: creating }),
    setTemporaryForecast: (forecast) => set({ temporaryForecast: forecast }),
    setSyncStateType: (type) => set((s) => ({ syncState: { ...s.syncState, type } })),
    setSyncStateId: (id) => set((s) => ({ syncState: { ...s.syncState, id } })),

    setCheckedGlobalForecasts: (forecasts) => set({ checkedGlobalForecasts: forecasts }),

    setCheckedGlobalTypeWells: (payload) => {
      const { checkedGlobalTypeWells, checkedTypeWells, originalCheckedGlobalTypeWells } =
        get();

      const noBackupYet = originalCheckedGlobalTypeWells.length === 0;
      const hasCurrentData = checkedGlobalTypeWells.length > 0;
      const currentIds = new Set(checkedGlobalTypeWells.map((tw) => tw.id));
      const newTypeWells = payload.filter((tw) => !currentIds.has(tw.id));

      const isFirstBackup = noBackupYet && hasCurrentData;
      const nextOriginal = isFirstBackup
        ? [...checkedGlobalTypeWells]
        : originalCheckedGlobalTypeWells;

      let nextCheckedTypeWells = checkedTypeWells;
      if (checkedTypeWells.length > 0) {
        const globalMap = new Map(payload.map((tw) => [tw.id, tw]));
        nextCheckedTypeWells = checkedTypeWells.map((tw) => {
          const global = globalMap.get(tw.id);
          if (
            global &&
            (global.color !== tw.color || global.thickness !== tw.thickness)
          ) {
            return { ...tw, color: global.color, thickness: global.thickness };
          }
          return tw;
        });
      }

      // Always append new type wells to the backup — including the first-backup
      // pass where nextOriginal is freshly captured from current state, because
      // the incoming payload may already contain new type wells not yet in
      // checkedGlobalTypeWells (e.g. setCheckedGlobalTypeWells([tw-1, tw-2])
      // when only tw-1 existed).
      const backupIds = new Set(nextOriginal.map((tw) => tw.id));
      const typeWellsToAdd = newTypeWells.filter((tw) => !backupIds.has(tw.id));
      const finalOriginal =
        typeWellsToAdd.length > 0 ? [...nextOriginal, ...typeWellsToAdd] : nextOriginal;

      set({
        checkedGlobalTypeWells: payload,
        checkedTypeWells: nextCheckedTypeWells,
        originalCheckedGlobalTypeWells: finalOriginal
      });
    },

    setOriginalCheckedGlobalTypeWells: (payload) =>
      set({
        originalCheckedGlobalTypeWells: payload.length > 0 ? [...payload] : payload
      }),

    setCheckedForecasts: (forecasts) => set({ checkedForecasts: forecasts }),
    setCheckedTypeWells: (typeWells) => set({ checkedTypeWells: typeWells }),
    setCheckedForecastKeys: (keys) => set({ checkedForecastKeys: keys }),
    setCheckedTypeWellKeys: (keys) => set({ checkedTypeWellKeys: keys }),

    setExpandedForecastKeys: (keys) => set({ expandedForecastKeys: keys }),
    setExpandedTypeWellKeys: (keys) => set({ expandedTypeWellKeys: keys }),

    setUseMcDanielForecasts: (use) => set({ useMcDanielForecasts: use }),
    setUseNoviForecasts: (use) => set({ useNoviForecasts: use }),
    setUseWorkingForecasts: (use) => set({ useWorkingForecasts: use }),

    setIsTypeWellSavable: (savable) => set({ isTypeWellSavable: savable }),
    setHasWellDataError: (hasError) => set({ hasWellDataError: hasError }),
    setHasTypeWellSaveSucceeded: (succeeded) =>
      set({ hasTypeWellSaveSucceeded: succeeded }),

    setBEditableSegments: (segments) => set({ bEditableSegments: segments }),
    setTypeWellTemplateNames: (names) => set({ typeWellTemplateNames: names }),

    reset: () => set(initialForecastState)
  }))
);
